import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  updateSession,
  updateSessionSeries,
  deleteSession,
  deleteSessionSeries,
  type ClubSession,
  type ClubLocation,
  type SeriesScope,
} from "../../services/api";
import DateInput from "../DateInput";

/**
 * Modificarea unui antrenament — inclusiv a unuia recurent.
 *
 * A coach who set up "Tuesday and Thursday at 17:00 for twelve weeks" and then
 * had the hall moved to 18:00 had nothing to do about it: the only edit in the
 * app changed one occurrence, so the honest answer was "delete the term and
 * build it again". That is the complaint this exists to answer.
 *
 * For a session in a series the dialog asks WHICH occurrences the change is
 * for, because the two meanings are genuinely different and guessing is
 * expensive either way:
 *
 *   - "Doar acest antrenament" — one Tuesday moved, the rest stay.
 *   - "Acesta și cele viitoare" — the schedule changed from here on. The
 *     default, because that is what "the training is at 18:00 now" means.
 *   - "Toată seria" — a correction to the whole thing, e.g. a title typo.
 *     Trainings that already happened keep their hour even here: the register
 *     was taken at that hour, and moving them would make it lie.
 */

type Mode = "single" | SeriesScope;

/** Local yyyy-mm-dd and HH:MM from an instant — the inputs' own format. */
function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    time: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
}

export default function SessionEditDialog({
  session,
  coaches,
  locations,
  onClose,
  onChanged,
}: {
  session: ClubSession;
  /** Offered only when the viewer may reassign — omit in the coach app. */
  coaches?: { id: string; full_name: string | null; email: string }[];
  locations?: ClubLocation[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const inSeries = !!session.is_recurring || !!session.recurring_parent_id;

  const start = useMemo(() => splitLocal(session.start_at), [session.start_at]);
  const end = useMemo(() => splitLocal(session.end_at), [session.end_at]);

  const [mode, setMode] = useState<Mode>(inSeries ? "following" : "single");
  const [title, setTitle] = useState(session.title);
  const [type, setType] = useState<"sport" | "educational">(session.type);
  const [date, setDate] = useState(start.date);
  const [startTime, setStartTime] = useState(start.time);
  const [endTime, setEndTime] = useState(end.time);
  const [coachId, setCoachId] = useState(session.coach_id || "");
  const [locationId, setLocationId] = useState(session.location_id || "");
  const [locationText, setLocationText] = useState(session.location_text || "");
  const [notes, setNotes] = useState(session.notes || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // On a series edit the date is fixed: the whole point is that each
  // occurrence keeps its own day and only the hour moves. Moving a series onto
  // another weekday is a different request, and the server refuses it rather
  // than silently shifting a term by a day.
  const dateLocked = mode !== "single";
  const effectiveDate = dateLocked ? start.date : date;

  const timesValid = endTime > startTime;
  const valid = !!title.trim() && !!effectiveDate && timesValid;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        type,
        start_at: `${effectiveDate}T${startTime}`,
        end_at: `${effectiveDate}T${endTime}`,
        ...(coaches && coachId ? { coach_id: coachId } : {}),
        ...(locations ? { location_id: locationId || null } : {}),
        location_text: locationText.trim() || null,
        notes: notes.trim() || null,
      };

      if (mode === "single") {
        await updateSession(session.id, payload);
        toast.success("Antrenament modificat.");
      } else {
        const res = await updateSessionSeries(session.id, { ...payload, scope: mode });
        const parts = [`${res.updated} antrenamente modificate`];
        if (res.retimed > 0) parts.push(`${res.retimed} mutate la ora nouă`);
        if (res.skipped_past > 0) parts.push(`${res.skipped_past} deja ținute, lăsate la ora lor`);
        toast.success(parts.join(" · "));
      }
      onChanged();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || "Nu am putut salva modificarea.");
    }
    setSaving(false);
  };

  const remove = async () => {
    const question =
      mode === "single" || !inSeries
        ? "Ștergi acest antrenament? Prezențele pontate la el se pierd."
        : mode === "all"
          ? "Anulezi toată seria? Antrenamentele deja ținute rămân, cele viitoare se șterg."
          : "Anulezi acest antrenament și pe cele de după el?";
    if (!window.confirm(question)) return;

    setDeleting(true);
    try {
      if (mode === "single" || !inSeries) {
        await deleteSession(session.id);
        toast.success("Antrenament șters.");
      } else {
        const res = await deleteSessionSeries(session.id, mode);
        toast.success(
          res.kept_past > 0
            ? `${res.deleted} antrenamente anulate · ${res.kept_past} deja ținute, păstrate`
            : `${res.deleted} antrenamente anulate.`
        );
      }
      onChanged();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || "Nu am putut șterge.");
    }
    setDeleting(false);
  };

  const field = "w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800";
  const label = "block mb-1 text-xs font-semibold text-slate-600";

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-y-auto bg-black/50">
      <form onSubmit={save} className="w-full max-w-lg my-8 bg-white shadow-xl rounded-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800">Modifică antrenamentul</h3>
            <p className="text-xs text-slate-500">
              {session.team?.name}
              {inSeries ? " · parte dintr-o serie recurentă" : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:bg-slate-100"
            aria-label="Închide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Which occurrences. First, because it changes what the rest of the
              form means. */}
          {inSeries && (
            <div className="p-3 border rounded-xl border-indigo-200 bg-indigo-50/60">
              <span className="block mb-2 text-xs font-bold tracking-wide uppercase text-indigo-700">
                Se aplică la
              </span>
              <div className="space-y-1.5">
                {([
                  ["following", "Acesta și cele viitoare", "Programul s-a schimbat de acum înainte."],
                  ["single", "Doar acest antrenament", "O singură dată mutată; restul seriei rămâne."],
                  ["all", "Toată seria", "Inclusiv cele trecute — ora lor nu se schimbă."],
                ] as const).map(([value, labelText, hint]) => (
                  <label key={value} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="series-scope"
                      checked={mode === value}
                      onChange={() => setMode(value as Mode)}
                      className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-800">{labelText}</span>
                      <span className="block text-[11px] text-slate-500">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className={label} htmlFor="se-title">Titlu</label>
            <input id="se-title" value={title} onChange={(e) => setTitle(e.target.value)} className={field} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="se-type">Tip</label>
              <select
                id="se-type"
                value={type}
                onChange={(e) => setType(e.target.value as "sport" | "educational")}
                className={field}
              >
                <option value="sport">Sportiv</option>
                <option value="educational">Educațional</option>
              </select>
            </div>
            {coaches && coaches.length > 0 && (
              <div>
                <label className={label} htmlFor="se-coach">Antrenor</label>
                <select id="se-coach" value={coachId} onChange={(e) => setCoachId(e.target.value)} className={field}>
                  <option value="">Neschimbat</option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name || c.email}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className={label} htmlFor="se-date">Data</label>
              <DateInput
                id="se-date"
                mode="date"
                value={effectiveDate}
                onChange={setDate}
                disabled={dateLocked}
                className={`${field} ${dateLocked ? "bg-slate-100 text-slate-400" : ""}`}
              />
            </div>
            <div>
              <label className={label} htmlFor="se-start">De la</label>
              <input
                id="se-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="se-end">Până la</label>
              <input
                id="se-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className={field}
              />
            </div>
          </div>
          {!timesValid && (
            <p className="text-xs text-rose-600">Ora de final trebuie să fie după ora de început.</p>
          )}
          {dateLocked && (
            <p className="text-xs text-slate-500">
              Pentru serie se schimbă doar ora — fiecare antrenament rămâne în ziua lui. Ca să muți
              unul pe altă zi, alege „Doar acest antrenament".
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {locations && (
              <div>
                <label className={label} htmlFor="se-loc">Locație</label>
                <select id="se-loc" value={locationId} onChange={(e) => setLocationId(e.target.value)} className={field}>
                  <option value="">Fără locație din listă</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className={label} htmlFor="se-loctext">Sau scrie unde</label>
              <input
                id="se-loctext"
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder="opțional"
                className={field}
              />
            </div>
          </div>

          <div>
            <label className={label} htmlFor="se-notes">Note</label>
            <textarea
              id="se-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${field} resize-none`}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={remove}
            disabled={deleting || saving}
            className="px-3 py-2 text-sm font-semibold rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            {deleting
              ? "Se șterge..."
              : mode === "single" || !inSeries
                ? "Șterge"
                : mode === "all"
                  ? "Anulează toată seria"
                  : "Anulează de aici înainte"}
          </button>
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Renunță
            </button>
            <button
              type="submit"
              disabled={!valid || saving || deleting}
              className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Se salvează..." : "Salvează"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
